import { type Discussions } from '@air/api';
import { formatFullName, formatInitials } from '@air/utilities';

import { type WorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';

export type WorkspaceAgentCommentPill = Extract<WorkspaceAgentMessagePill, { type: 'comment' }>;

type WorkspaceAgentGetCommentResponse = Awaited<ReturnType<typeof Discussions.Comments.get>>;

const FALLBACK_COMMENT_AUTHOR_NAME = 'Comment';

/**
 * A moniker pill repeats the comment id in the fields the card type requires. Those stand-ins are
 * not a clip, so the card stays closed until `Discussions.Comments.get` places the thread.
 */
export const workspaceAgentCommentPillCanOpen = (pill: WorkspaceAgentCommentPill): boolean =>
  pill.discussionId !== pill.commentId && pill.versionId !== pill.commentId;

type ToWorkspaceAgentCommentPillFromGetCommentParams = {
  comment: WorkspaceAgentGetCommentResponse;
  pill: WorkspaceAgentCommentPill;
};

export const toWorkspaceAgentCommentPillFromGetComment = ({
  comment,
  pill,
}: ToWorkspaceAgentCommentPillFromGetCommentParams): WorkspaceAgentCommentPill => {
  const authorName =
    formatFullName(comment.account.firstName, comment.account.lastName) || FALLBACK_COMMENT_AUTHOR_NAME;
  const authorInitials = formatInitials(comment.account.firstName, comment.account.lastName);
  const assetId = comment.assetId ?? (pill.assetId !== pill.commentId ? pill.assetId : undefined);
  const versionId = comment.clipId ?? (pill.versionId !== pill.commentId ? pill.versionId : undefined);

  return {
    assetId: assetId ?? pill.commentId,
    authorName,
    body: comment.body,
    commentId: comment.id,
    createdAt: comment.createdAt,
    discussionId: comment.discussionId,
    id: pill.id,
    type: 'comment',
    versionId: versionId ?? pill.commentId,
    ...(authorInitials ? { authorInitials } : {}),
    ...(pill.staleVersion === undefined ? {} : { staleVersion: pill.staleVersion }),
  };
};
